import { GripVertical, Lock } from 'lucide-react';
import mergeRefs from 'merge-refs';
import {
  createContext,
  forwardRef,
  useContext,
  type HTMLAttributes,
  type ReactNode
} from 'react';
import { Button, type ButtonProps } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';

export type EntityTableRowDndHandleProps = {
  attributes: HTMLAttributes<HTMLElement>;
  listeners: HTMLAttributes<HTMLElement> | undefined;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  disabled: boolean;
  isDragging: boolean;
};

const EntityTableRowDndContext =
  createContext<EntityTableRowDndHandleProps | null>(null);

export function EntityTableRowDndProvider({
  value,
  children
}: {
  value: EntityTableRowDndHandleProps | null;
  children: React.ReactNode;
}) {
  return (
    <EntityTableRowDndContext.Provider value={value}>
      {children}
    </EntityTableRowDndContext.Provider>
  );
}

export function useEntityTableRowDnd(): EntityTableRowDndHandleProps | null {
  return useContext(EntityTableRowDndContext);
}

export type EntityTableDragHandleProps = Omit<
  ButtonProps,
  'children' | 'type' | 'layout' | 'variant' | 'size'
> & {
  'aria-label': string;
};

/**
 * Grip handle for {@link SortableEntityTable} row reorder.
 * Must render inside a sortable row (reads activator props from context).
 *
 * Keyboard (dnd-kit): focus the handle, press Space to pick up, Arrow keys to
 * move, Space or Escape to drop — do not intercept Space/Enter here.
 */
export const EntityTableDragHandle = forwardRef<
  HTMLButtonElement,
  EntityTableDragHandleProps
>(function EntityTableDragHandle(
  { className, 'aria-label': ariaLabel, onClick, onKeyDown, ...rest },
  ref
) {
  const dnd = useEntityTableRowDnd();
  if (!dnd || dnd.disabled) {
    return <span className="inline-block size-3.5" aria-hidden="true" />;
  }

  const listenerKeyDown = dnd.listeners?.onKeyDown;

  return (
    <Button
      ref={mergeRefs<HTMLButtonElement>(dnd.setActivatorNodeRef, ref)}
      type="button"
      layout="icon"
      variant="headless"
      size="sm"
      aria-label={ariaLabel}
      className={cn(
        'size-7 min-h-0 min-w-0 cursor-grab touch-none text-fg-muted active:cursor-grabbing',
        dnd.isDragging && 'cursor-grabbing',
        className
      )}
      {...dnd.attributes}
      {...dnd.listeners}
      {...rest}
      onClick={event => {
        event.stopPropagation();
        onClick?.(event);
      }}
      onKeyDown={event => {
        // Preserve dnd-kit KeyboardSensor (Space + arrows); `listeners` were
        // spread above but this prop overrides that handler.
        if (typeof listenerKeyDown === 'function') {
          listenerKeyDown(event);
        }
        onKeyDown?.(event);
      }}
    >
      <GripVertical className="size-3.5" />
    </Button>
  );
});

EntityTableDragHandle.displayName = 'EntityTableDragHandle';

export type EntityTableLockedHandleProps = {
  'aria-label': string;
  className?: string;
};

/** Non-interactive lock placeholder in the drag column for pinned / locked rows. */
export function EntityTableLockedHandle({
  'aria-label': ariaLabel,
  className
}: EntityTableLockedHandleProps) {
  return (
    <span
      className={cn(
        'inline-flex size-7 items-center justify-center text-fg-muted',
        className
      )}
      aria-label={ariaLabel}
    >
      <Lock className="size-3.5" aria-hidden="true" />
    </span>
  );
}

export type EntityTableDragOverlayProps = {
  children: ReactNode;
  className?: string;
};

/** Shared chrome for row-reorder drag overlays (matches table handle affordance). */
export function EntityTableDragOverlay({
  children,
  className
}: EntityTableDragOverlayProps) {
  return (
    <div
      className={cn(
        'flex cursor-grabbing select-none items-center gap-2 rounded-sm bg-bg-subtle/90 px-2 py-1.5 text-sm shadow-md',
        className
      )}
    >
      <GripVertical className="size-3.5 shrink-0 text-fg-muted" aria-hidden />
      <span className="min-w-0 truncate font-medium">{children}</span>
    </div>
  );
}
